// Suppression d'un élément d'index : sa sortie, au lieu d'une disparition sèche.
//
// Le serveur répond par `turbo_stream.remove dom_id(resource, 'preview')`
// (resources/_destroy.turbo_stream.erb) et Turbo retirait la ligne sur-le-champ : les
// suivantes sautaient d'une hauteur de ligne, et l'œil perdait le fil de ce qui venait de
// partir. On emballe le rendu de ce stream (`turbo:before-stream-render`) pour jouer d'abord le
// geste du fil de notifications marquées comme lues : la ligne glisse et s'efface, puis la place
// qu'elle laisse se replie — les suivantes remontent au lieu de sauter. Turbo ne retire
// l'élément qu'ensuite, par son action normale.
//
// Rien d'optimiste : l'animation part à l'arrivée de la réponse. Une suppression refusée
// (enregistrement encore référencé) ne renvoie que le flash d'alerte, sans `remove` : la ligne
// ne bouge pas.
//
// Les trois dispositions d'index n'ont pas la même ligne à faire partir :
//   • tableau (`row_layout: :table`) — le <tr>, et sa ligne de panneaux dépliables s'il en a
//     une : laissée seule, elle survivait à la ligne qu'elle détaille ;
//   • lignes (disposition par défaut) — la turbo-frame d'aperçu, ou la colonne Bootstrap qui ne
//     contient qu'elle : Turbo ne retire que la frame, et la colonne restée vide gardait sa
//     gouttière et privait la vraie dernière ligne de ses styles de fin de liste ;
//   • cartes (`row_layout: :cards`) — la carte s'estompe sur place. Pas de repli : dans les
//     colonnes CSS du masonry, les cartes suivantes changent de colonne. Chacune glisse donc de
//     son ancienne place à la nouvelle (FLIP).
//
// Durées et courbes vivent dans `_index_row_leave.scss` ; on attend ici la fin des animations
// que les classes ont lancées, sans les redéclarer.

const RESULTS_SELECTOR = '.index-search-results';
const TABLE_ROW_SELECTOR = 'tr.index-scroll-table__row';
const COLLAPSE_ROW_SELECTOR = 'tr.index-scroll-table__collapse-row';
const CARD_SELECTOR = '.index-preview-cards__item';
const GRID_COLUMN_SELECTOR = '.row > [class*="col"]';

// Filet des attentes : un onglet passé en arrière-plan en pleine sortie ne fait plus avancer
// les animations, et leurs promesses `finished` ne se résolvent plus.
const SAFETY_MARGIN_MS = 150;

// Rendus en cours, par élément sortant : un second `remove` de la même cible attend la fin du
// premier au lieu de lancer une seconde sortie par-dessus.
const leaving = new WeakMap<Element, Promise<void>>();

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const settled = async (elements: Element[]) => {
  const animations = elements.flatMap((element) => element.getAnimations());
  const endTimes = animations
    .map((animation) => Number(animation.effect?.getComputedTiming().endTime))
    .filter(Number.isFinite);
  if (endTimes.length === 0) return;

  await Promise.race([
    Promise.allSettled(animations.map((animation) => animation.finished)),
    wait(Math.max(...endTimes) + SAFETY_MARGIN_MS),
  ]);
};

// La place laissée se replie : hauteur figée en pixels, puis ramenée à zéro — une transition ne
// part pas de `auto`. Un <tr> ne descend pas sous la hauteur de ses cellules : elles partent
// d'abord (elles sont déjà invisibles), et la ligne vide garde la hauteur qu'on lui fige.
const collapse = async (boxes: HTMLElement[]) => {
  const heights = boxes.map((box) => box.getBoundingClientRect().height);
  boxes.forEach((box, index) => {
    box.style.height = `${heights[index]}px`;
    box.classList.add('index-row-emptied');
  });
  void boxes[0].offsetHeight; // la hauteur de départ doit être appliquée avant la classe

  boxes.forEach((box) => box.classList.add('index-row-collapsing'));
  await settled(boxes);
};

// Les colonnes d'un tableau se calent sur leur contenu le plus large. Si c'était celui de la
// ligne qui part, elles se resserraient d'un coup au début du repli, en plein mouvement : leurs
// largeurs restent figées (par l'en-tête) le temps du repli, et se réajustent une fois la ligne
// retirée.
const pinColumnWidths = (row: HTMLElement) => {
  const cells = [...(row.closest('table')?.tHead?.rows[0]?.cells ?? [])];
  const widths = cells.map((cell) => cell.getBoundingClientRect().width);
  const previous = cells.map((cell) => cell.style.minWidth);
  cells.forEach((cell, index) => (cell.style.minWidth = `${widths[index]}px`));

  return () => cells.forEach((cell, index) => (cell.style.minWidth = previous[index]));
};

// La ligne à faire partir, et ce qu'il reste à ranger une fois que Turbo l'a retirée.
const leaveRow = async (target: HTMLElement): Promise<() => void> => {
  const boxes: HTMLElement[] = [];
  const tableRow = target.matches(TABLE_ROW_SELECTOR);

  if (tableRow) {
    boxes.push(target);
    const panels = target.nextElementSibling;
    if (panels instanceof HTMLElement && panels.matches(COLLAPSE_ROW_SELECTOR)) boxes.push(panels);
  } else {
    const column = target.parentElement;
    const alone = column?.matches(GRID_COLUMN_SELECTOR) && column.childElementCount === 1;
    boxes.push(alone ? column! : target);
  }

  boxes.forEach((box) => {
    box.inert = true;
    box.classList.add('index-row-leaving');
  });
  await settled(boxes);

  const releaseColumns = tableRow ? pinColumnWidths(target) : null;
  await collapse(boxes);

  return () => {
    // Turbo n'a retiré que sa cible : ses compagnons (panneaux, colonne vide) partent avec elle.
    boxes.forEach((box) => box !== target && box.remove());
    releaseColumns?.();
  };
};

const leaveCard = async (card: HTMLElement): Promise<() => void> => {
  card.inert = true;
  card.classList.add('index-card-leaving');
  await settled([card]);

  const others = [...(card.parentElement?.children ?? [])].filter(
    (element): element is HTMLElement => element !== card && element instanceof HTMLElement && element.matches(CARD_SELECTOR)
  );
  const before = others.map((element) => element.getBoundingClientRect());

  return () => {
    // Lectures d'abord, écritures ensuite : un seul calcul de mise en page pour toute la grille.
    const offsets = others.map((element, index) => {
      const after = element.getBoundingClientRect();
      return [before[index].left - after.left, before[index].top - after.top];
    });
    const moved = others.filter((element, index) => {
      const [dx, dy] = offsets[index];
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return false;

      element.style.translate = `${dx}px ${dy}px`;
      return true;
    });
    if (moved.length === 0) return;

    void moved[0].offsetWidth; // la position d'avant doit être peinte comme point de départ
    moved.forEach((element) => {
      element.classList.add('index-card-settling');
      element.style.translate = '';
    });
    settled(moved).then(() => moved.forEach((element) => element.classList.remove('index-card-settling')));
  };
};

export function initializeIndexRowLeave() {
  if ((window as any).__indexRowLeaveInitialized) return;
  (window as any).__indexRowLeaveInitialized = true;

  document.addEventListener('turbo:before-stream-render', (event) => {
    const detail = (event as CustomEvent).detail;
    const stream = detail?.newStream as Element | undefined;
    if (stream?.getAttribute('action') !== 'remove') return;

    const target = document.getElementById(stream.getAttribute('target') || '');
    if (!target?.id.startsWith('preview_') || !target.closest(RESULTS_SELECTOR)) return;

    const card = target.matches(CARD_SELECTOR);
    if (!card && !target.matches(`${TABLE_ROW_SELECTOR}, turbo-frame`)) return;

    // Personne pour la voir partir (onglet masqué), ou moins de mouvement demandé : Turbo
    // retire la ligne d'un coup, comme avant.
    if (document.hidden || prefersReducedMotion()) return;

    const originalRender = detail.render;
    detail.render = (streamElement: Element) => {
      const inFlight = leaving.get(target);
      if (inFlight) return inFlight.then(() => originalRender(streamElement));

      // L'enregistrement est déjà supprimé : quoi qu'il arrive à l'animation, la ligne part.
      const rendering = (async () => {
        let tidy: (() => void) | undefined;
        try {
          tidy = await (card ? leaveCard(target) : leaveRow(target));
        } finally {
          await originalRender(streamElement);
        }
        tidy();
      })();
      leaving.set(target, rendering);
      return rendering;
    };
  });

  // Seul le tableau de bord est mis en cache (cf. initializeTurboCachePolicy) : une ligne encore
  // en train de partir y serait photographiée, puis restituée invisible au retour arrière.
  document.addEventListener('turbo:before-cache', () => {
    document.querySelectorAll('.index-row-leaving, .index-card-leaving').forEach((element) => element.remove());
  });
}
